import { useState } from "react";
import { Head, Link, router, usePage } from "@inertiajs/react";
import { ArrowRight, CheckCircle2, CreditCard, Heart, School, Users } from "lucide-react";
import FaqCard from "@/components/marketing/FaqCard";
import MarketingLayout from "@/components/marketing/MarketingLayout";
import PageContainer from "@/components/marketing/PageContainer";
import SectionHeading from "@/components/marketing/SectionHeading";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Notice } from "@/components/ui/notice";

const ROLES = [
  {
    role: "school_admin",
    icon: School,
    title: "Campaign Organizer",
    // Short enough to fit the button when the three cards sit side by side.
    button: "Explore as an organizer",
    description: "The whole school at a glance: totals, classrooms, donations, and prizes.",
  },
  {
    role: "teacher",
    icon: Users,
    title: "Teacher",
    description: "One classroom's reading, its leaderboard, and the prizes to hand out.",
  },
  {
    role: "parent",
    icon: Heart,
    title: "Parent",
    description: "Two kids' reading, their goals, and the donation pages families share.",
  },
];

// What each view shows. Plain text on purpose: it's the page's
// crawlable description of the product, not just a row of buttons.
const TOUR = [
  {
    icon: School,
    title: "What campaign organizers see",
    points: [
      "Money raised and minutes read, live, against the school's goals",
      "Classroom and student leaderboards",
      "Every donation, including the card fees donors chose to cover",
      "School rewards unlocking as the totals climb",
      "Printable take-home sheets with each family's QR code",
    ],
  },
  {
    icon: Users,
    title: "What teachers see",
    points: [
      "One form to log a reading session for the whole class",
      "Who's reading at home, student by student",
      "Which students have earned prizes, and which are handed out",
      "Class goals and class rewards",
    ],
  },
  {
    icon: Heart,
    title: "What parents see",
    points: [
      "Each child's minutes, goals, and prizes",
      "Reading logged from home in a few taps",
      "A donation page to share with family and friends",
      "Siblings side by side under one account",
    ],
  },
  {
    icon: CreditCard,
    title: "What donors see",
    points: [
      "A student's page with their progress and supporters",
      "Card donations with no account needed",
      "The option to cover the card fee so the school gets the full gift",
      "A receipt as soon as they give",
    ],
  },
];

/**
 * /demo: pick a role and get signed straight in to the demo school, or
 * open the donation pages donors see. Nothing is saved. The copy below the
 * buttons is what search engines index, and the FAQ doubles as FAQPage
 * JSON-LD (DemosController::FAQS).
 */
export default function DemoShow({ faqs = [], available, school_name, student_first_name, student_page_path, school_page_path }) {
  const { routes } = usePage().props;
  const [starting, setStarting] = useState(null);

  const start = (role) => {
    setStarting(role);
    router.post(routes.demo, { role }, { onFinish: () => setStarting(null) });
  };

  return (
    <MarketingLayout>
      <Head title="Read-a-thon software demo" />

      <PageContainer width="default" className="pt-10 md:pt-14 pb-8 space-y-10">
        <SectionHeading
          as="h1"
          flourish="read-a-thon software demo"
          title="See a read-a-thon in full swing."
          subhead={`Try PagePledge the way a school uses it. ${school_name || "Our demo school"} is halfway through a two-week read-a-thon: pick a view and look around. There's nothing to sign up for, and nothing you change is saved.`}
        />

        {!available && <Notice>The demo is being refreshed. Check back in a few minutes.</Notice>}

        <div className="grid gap-4 md:grid-cols-3">
          {ROLES.map(({ role, icon: Icon, title, button, description }) => (
            <Card key={role} className="flex flex-col">
              <CardHeader>
                <CardTitle as="h2" className="flex items-center gap-2">
                  <Icon className="h-5 w-5 text-primary" aria-hidden="true" />
                  {title}
                </CardTitle>
                <CardDescription className="leading-relaxed">{description}</CardDescription>
              </CardHeader>
              <CardContent className="mt-auto">
                <Button className="w-full" variant="outline" disabled={!available || starting !== null} onClick={() => start(role)}>
                  {starting === role ? "Opening…" : button || `Explore as a ${title.toLowerCase()}`}
                  <ArrowRight className="h-4 w-4" aria-hidden="true" />
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>

        {available && (
          <div className="rounded-xl border border-border bg-card p-6 text-center space-y-3">
            <h2 className="text-2xl text-foreground">Or see what donors see</h2>
            <p className="mx-auto max-w-xl text-muted-foreground">
              Donors never need an account. Try the donation form: it takes a pretend card, so nothing is charged.
            </p>
            <div className="flex flex-wrap justify-center gap-3 pt-1">
              {student_page_path && (
                <Button variant="outline" asChild>
                  <Link href={student_page_path}>{student_first_name}'s donation page</Link>
                </Button>
              )}
              <Button variant="outline" asChild>
                <Link href={school_page_path}>The school's donation page</Link>
              </Button>
            </div>
          </div>
        )}
      </PageContainer>

      <PageContainer width="wide" className="pt-10">
        <SectionHeading
          flourish="inside the demo"
          title="A school halfway through its read-a-thon."
          subhead="Six classrooms, over a hundred students, a week of reading logged by teachers and parents, and donations from families and local businesses. Here's what each person sees."
        />
        <div className="mt-10 grid gap-5 md:grid-cols-2">
          {TOUR.map(({ icon: Icon, title, points }) => (
            <Card key={title} className="bg-card/70">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-xl">
                  <Icon className="h-5 w-5 text-primary" aria-hidden="true" />
                  {title}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2.5">
                  {points.map((point) => (
                    <li key={point} className="flex gap-3 text-sm text-muted-foreground">
                      <CheckCircle2 className="h-5 w-5 shrink-0 text-success" aria-hidden="true" />
                      <span>{point}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          ))}
        </div>
      </PageContainer>

      <PageContainer width="wide" className="pt-12">
        <SectionHeading flourish="common questions" title="About the demo" />
        <div className="mt-10 grid gap-4 md:grid-cols-2">
          {faqs.map((faq) => (
            <FaqCard key={faq.question} as="h3" question={faq.question} answer={faq.answer} />
          ))}
        </div>
      </PageContainer>

      <PageContainer width="narrow" className="text-center space-y-6 pt-12 pb-12 md:pb-16">
        <SectionHeading
          flourish="your turn"
          title="Ready to run your own read-a-thon?"
          subhead="The setup guide walks you through campaign dates, classrooms, payments, and take-home sheets. A flat campaign fee, with no PagePledge transaction fees."
        />
        <div className="flex flex-wrap justify-center gap-3">
          <Button size="lg" asChild>
            <Link href={routes.new_school_application}>
              Sign up your school
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Link>
          </Button>
          <Button size="lg" variant="outline" asChild>
            <Link href={routes.how_it_works}>See how it works</Link>
          </Button>
        </div>
        <p className="text-sm text-muted-foreground">
          Questions about cost? <Link href={routes.pricing} className="font-semibold text-primary hover:underline">See pricing</Link>
        </p>
      </PageContainer>
    </MarketingLayout>
  );
}
